import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BodyM, DisplayL } from '@sorare/core/src/atoms/typography';
import { useProbabilisticBundleConfigSummary } from '@sorare/core/src/hooks/useProbabilisticBundleConfigSummary';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { RewardName } from '../../RewardName';
import { BoldWhite } from '../BoldWhite';
import { Rewards_PromotionalEventInterface } from './__generated__/index.graphql';
import boostUrl from './assets/boost.png';
import cardsUrl from './assets/cards.png';
import jerseyUrl from './assets/jersey.png';
import raffleUrl from './assets/raffle.png';

const Wrapper = styled(Vertical).attrs({ center: true })``;

const Secondary = styled(Vertical).attrs({ center: true })`
  gap: 0;
  margin-top: calc(-1 * var(--triple-unit));
`;

type Props = { event: Rewards_PromotionalEventInterface };

const sortRewards = (summary: Record<string, number>) =>
  sortBy(
    r => r.probability,
    Object.entries(summary).map(([name, probability]) => ({
      name,
      probability,
    }))
  );

export const Reward = ({ name }: { name: string }) => {
  const { formatMessage } = useIntl();
  if (name === formatMessage(glossary.cards)) {
    return (
      <Vertical center gap={0.5}>
        <RewardName>
          <FormattedMessage
            id="NeonChristmas.explanation.reward.cards"
            defaultMessage="2nd player"
          />
        </RewardName>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage
            id="NeonChristmas.explanation.reward.cards.subtext"
            defaultMessage="from the same club*"
          />
        </BodyM>
        <img src={cardsUrl} alt="" />
      </Vertical>
    );
  }
  if (name === formatMessage(fantasy.credits)) {
    <RewardName>
      <FormattedMessage
        id="NeonChristmas.explanation.reward.credits"
        defaultMessage="Conversion credits"
      />
    </RewardName>;
  }
  if (name === 'Jersey') {
    return <img src={jerseyUrl} alt="" />;
  }
  if (name === 'Raffle Ticket') {
    return <img src={raffleUrl} alt="" />;
  }
  if (name === 'Level Up - Limited') {
    return (
      <>
        <RewardName>Level up</RewardName>
        <img src={boostUrl} alt="" />
      </>
    );
  }

  return <DisplayL />;
};

export const Rewards = ({ event }: Props) => {
  const summary = useProbabilisticBundleConfigSummary(
    event.rewardsProbabilisticBundleConfig[0]
  );
  const [firstReward, secondReward] = sortRewards(summary);

  return (
    <Wrapper>
      <BodyM color="var(--c-nd-600)">
        <FormattedMessage
          id="NeonChristmas.explanation.reward.percentage"
          defaultMessage="<b>{probability, number, percent}</b> chance of winning a"
          values={{ probability: firstReward.probability, b: BoldWhite }}
        />
      </BodyM>

      <Reward name={firstReward.name} />

      {secondReward && (
        <Secondary>
          <BodyL bold>
            <FormattedMessage
              id="NeonChristmas.explanation.reward.or"
              defaultMessage="Or get"
            />
          </BodyL>
          <Reward name={secondReward.name} />
        </Secondary>
      )}
    </Wrapper>
  );
};

Rewards.fragments = {
  PromotionalEventInterface: gql`
    fragment Rewards_PromotionalEventInterface on PromotionalEventInterface {
      slug
      id
      rewardsProbabilisticBundleConfig {
        id
        ...useProbabilisticBundleConfigSummary_ProbabilisticBundleConfig
      }
    }
    ${useProbabilisticBundleConfigSummary.fragments.ProbabilisticBundleConfig}
  ` as TypedDocumentNode<Rewards_PromotionalEventInterface>,
};
